import React, { useState, useEffect } from 'react';
import { peopleAPI, getImageUrl } from '../../services/api';
import Pagination from '../Pagination';
import './PersonSelectorDialog.css';

interface Person {
  _id: string;
  key: string;
  organization_id: string;
  image_url?: string;
  created_at: string;
}

interface PersonSelectorDialogProps {
  show: boolean;
  organizationId: string;
  selectedPersonId?: string;
  onSelect: (person: Person) => void;
  onClose: () => void;
}

function PersonSelectorDialog({
  show,
  organizationId,
  selectedPersonId,
  onSelect,
  onClose
}: PersonSelectorDialogProps) {
  const [people, setPeople] = useState<Person[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchKey, setSearchKey] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortBy, setSortBy] = useState<'key' | 'created_at'>('key');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const loadPeople = async () => {
    if (!organizationId) return;
    
    setLoading(true);
    try {
      const response = await peopleAPI.list(organizationId, sortBy, sortOrder);
      setPeople(response.data);
    } catch (error) {
      console.error('Erro ao carregar pessoas:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show && organizationId) {
      loadPeople();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, organizationId, sortBy, sortOrder]);

  // Filtrar e paginar pessoas
  const filteredPeople = people.filter(person =>
    person.key.toLowerCase().includes(searchKey.toLowerCase()) ||
    person._id.toLowerCase().includes(searchKey.toLowerCase())
  );
  
  const totalPages = Math.ceil(filteredPeople.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedPeople = filteredPeople.slice(startIndex, startIndex + itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchKey]);

  const handleSelect = (person: Person) => {
    onSelect(person);
    onClose();
  };

  const handleClearSelection = () => {
    onSelect({ _id: '', key: '', organization_id: '', created_at: '' } as Person);
    onClose();
  };

  if (!show) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content person-selector-modal" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h2>👤 Selecionar Pessoa</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body">
          {/* Barra de busca e ordenação */}
          <div className="search-bar">
            <div className="search-input-wrapper">
              <input
                type="text"
                placeholder="🔍 Buscar por chave ou ID..."
                value={searchKey}
                onChange={(e) => setSearchKey(e.target.value)}
                className="search-input"
              />
              {searchKey && (
                <button 
                  className="clear-search"
                  onClick={() => setSearchKey('')}
                  title="Limpar busca"
                >
                  ✕
                </button>
              )}
            </div>
            
            <select 
              className="sort-select"
              value={`${sortBy}-${sortOrder}`}
              onChange={(e) => {
                const [field, order] = e.target.value.split('-') as ['key' | 'created_at', 'asc' | 'desc'];
                setSortBy(field);
                setSortOrder(order);
              }}
            >
              <option value="key-asc">Chave (A-Z)</option>
              <option value="key-desc">Chave (Z-A)</option>
              <option value="created_at-desc">Mais recentes</option>
              <option value="created_at-asc">Mais antigas</option>
            </select>
            
            <span className="search-count">
              {filteredPeople.length} pessoa{filteredPeople.length !== 1 ? 's' : ''}
            </span>
          </div>

          {/* Botão para limpar seleção */}
          {selectedPersonId && (
            <div className="clear-selection-wrapper">
              <button 
                className="btn-clear-selection"
                onClick={handleClearSelection}
              >
                🔄 Buscar em todas as pessoas
              </button>
            </div>
          )}

          {/* Tabela de pessoas */}
          {loading ? (
            <p>Carregando...</p>
          ) : (
            <>
              <div className="table-container">
                <table className="people-table">
                  <thead>
                    <tr>
                      <th>Foto</th>
                      <th>Chave</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedPeople.length > 0 ? (
                      paginatedPeople.map((person) => (
                        <tr 
                          key={person._id}
                          className={selectedPersonId === person._id ? 'selected' : ''}
                        >
                          <td className="photo-cell">
                            {person.image_url ? (
                              <img 
                                src={getImageUrl(person.image_url)} 
                                alt={person.key}
                                className="person-photo"
                              />
                            ) : (
                              <div className="no-photo">👤</div>
                            )}
                          </td>
                          <td>
                            <span className="person-key">{person.key}</span>
                            {selectedPersonId === person._id && (
                              <span className="current-badge">Atual</span>
                            )}
                            <br />
                            <span className="org-id-label">{person._id}</span>
                          </td>
                          <td className="actions-cell">
                            <button 
                              className="btn-action btn-select-icon"
                              onClick={() => handleSelect(person)}
                              title={selectedPersonId === person._id ? 'Pessoa atual' : 'Selecionar esta pessoa'}
                              disabled={selectedPersonId === person._id}
                            >
                              {selectedPersonId === person._id ? '✓' : '➜'}
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={3} className="no-results">
                          Nenhuma pessoa encontrada{searchKey && ` com a chave "${searchKey}"`}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Paginação */}
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default PersonSelectorDialog;
